import React from 'react';
import { TrendingUp, TrendingDown, Check } from 'lucide-react';
import { DesvioDaRota as Desvio } from '../types';

/**
 * Quanto a viagem rodou a mais, ou a menos, do que a rota do mapa previa.
 *
 * É a pergunta que a frota faz todo dia — "por que este trajeto rodou 40 km a
 * mais?" — e que até agora exigia abrir duas telas e fazer a conta de cabeça.
 *
 * **Só a gestão vê.** O motorista informa o hodômetro; se ele enxergasse o
 * quanto passou do previsto, o número deixaria de ser leitura e viraria
 * negociação. Por isso o servidor nem manda este campo para o portal — não é
 * questão de esconder na tela.
 *
 * A faixa de tolerância existe porque desvio pequeno é a regra, não a exceção:
 * o mapa traça uma rota, o motorista pega outra igualmente válida, e o
 * hodômetro conta o que rodou dentro do pátio. Pintar 3% de vermelho ensinaria
 * o gestor a ignorar a cor — e aí a cor deixa de servir para o caso de 40%.
 */

/** Até aqui é variação normal de trajeto, não desvio. */
const TOLERANCIA = 10;

/** Acima disto, não é escolha de caminho: é outra viagem. */
const GRAVE = 25;

const faixa = (percentual: number) => {
  const absoluto = Math.abs(percentual);

  if (absoluto <= TOLERANCIA) {
    return {
      cor: 'bg-green-100 text-green-800',
      corTexto: 'text-green-700',
      icone: Check,
      rotulo: 'Dentro do previsto',
    };
  }

  if (absoluto <= GRAVE) {
    return {
      cor: 'bg-amber-100 text-amber-800',
      corTexto: 'text-amber-700',
      icone: percentual > 0 ? TrendingUp : TrendingDown,
      rotulo: percentual > 0 ? 'Acima do previsto' : 'Abaixo do previsto',
    };
  }

  return {
    cor: 'bg-red-100 text-red-800',
    corTexto: 'text-red-700',
    icone: percentual > 0 ? TrendingUp : TrendingDown,
    rotulo: percentual > 0 ? 'Muito acima do previsto' : 'Muito abaixo do previsto',
  };
};

/** O número com sinal: "+20 km" diz mais do que "20 km". */
const comSinal = (valor: number, unidade: string) =>
  `${valor > 0 ? '+' : ''}${valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${unidade}`;

/** Versão compacta, para a listagem. */
export const SeloDeDesvio: React.FC<{ desvio?: Desvio | null }> = ({ desvio }) => {
  if (!desvio) return null;

  const { cor, icone: Icone } = faixa(desvio.percentual);

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cor}`}
      title={`Rodou ${comSinal(desvio.km, 'km')} (${comSinal(desvio.percentual, '%')}) em relação à rota do mapa`}
    >
      <Icone className="w-3 h-3" />
      {comSinal(desvio.km, 'km')}
    </span>
  );
};

/** Versão completa, para o detalhe da viagem. */
const DesvioDaRota: React.FC<{
  desvio?: Desvio | null;
  previstoKm?: number | null;
  rodadoKm?: number | null;
}> = ({ desvio, previstoKm, rodadoKm }) => {
  // Sem os dois números não há comparação, e um card vazio só ocuparia espaço.
  // Mas o motivo importa: quem lê precisa saber se falta a rota ou a chegada.
  if (!desvio) {
    if (previstoKm == null) return null;

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-sm text-gray-500">
          Rota prevista de {previstoKm.toLocaleString('pt-BR')} km. A comparação com o hodômetro aparece
          quando o motorista informar a quilometragem de chegada.
        </p>
      </div>
    );
  }

  const { cor, corTexto, icone: Icone, rotulo } = faixa(desvio.percentual);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className={`px-4 py-2 flex items-center gap-2 ${cor}`}>
        <Icone className="w-4 h-4" />
        <span className="text-sm font-semibold">{rotulo}</span>
      </div>

      <div className="px-4 py-3 grid grid-cols-3 gap-3 bg-white">
        <div>
          <dt className="text-xs font-medium text-gray-500">Rota do mapa</dt>
          <dd className="mt-0.5 text-sm text-gray-900">
            {previstoKm != null ? `${previstoKm.toLocaleString('pt-BR')} km` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-gray-500">Rodado no painel</dt>
          <dd className="mt-0.5 text-sm text-gray-900">
            {rodadoKm != null ? `${rodadoKm.toLocaleString('pt-BR')} km` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-gray-500">Diferença</dt>
          <dd className={`mt-0.5 text-sm font-semibold ${corTexto}`}>
            {comSinal(desvio.km, 'km')}
            <span className="font-normal"> ({comSinal(desvio.percentual, '%')})</span>
          </dd>
        </div>
      </div>
    </div>
  );
};

export default DesvioDaRota;
